import type { CSSProperties } from 'react';
import { cn } from '@worldcoo/ui/lib/utils';

/**
 * A paragraph the scroll READS OUT: every word starts dimmed and comes up to full strength as the block
 * travels through the viewport, the way a karaoke line fills in. The timing and the geometry live in
 * the `new-karaoke` block of `-styles/theme.css`; this only splits the text and numbers the words.
 *
 * `text` is a STRING and not `children`: splitting arbitrary nodes would mean walking a React tree and
 * rebuilding it, and a paragraph carrying inline markup — the closing sentence and its squiggle — is
 * exactly the case this should refuse rather than mangle.
 *
 * The trailing space rides inside each word's span. It is a break opportunity either way, so the
 * wrapping is unchanged, and it leaves no bare text nodes between the spans.
 */
export function Karaoke(props: { text: string; className?: string }) {
	// Keyed by word AND position, because a sentence repeats words. Built out here so the index is not
	// mistaken for a list ordering: this list only ever changes wholesale, on a language change.
	const words = props.text
		.trim()
		.split(/\s+/)
		.map((word, index) => ({ word, index, key: `${index}-${word}` }));

	return (
		<p className={cn('new-karaoke', props.className)} style={{ '--k-n': words.length } as CSSProperties}>
			{words.map((entry) => (
				<span key={entry.key} className="new-karaoke-word" style={{ '--k-i': entry.index } as CSSProperties}>
					{entry.word}
					{entry.index < words.length - 1 ? ' ' : ''}
				</span>
			))}
		</p>
	);
}
